import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import projectDashboardService from '../../services/projectDashboardService';
import projectService from '../../services/projectService';
import WorkflowAccessCard from './components/WorkflowAccessCard';
import ModuleProgressSummary from './components/ModuleProgressSummary';
import ModuleQAQCTracking from './components/ModuleQAQCTracking';
import ModuleResourceManagement from './components/ModuleResourceManagement';
import Breadcrumb from './components/Breadcrumb';

const ModuleLandingPage = () => {
  const { projectId, moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [project, setProject] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [qaqcData, setQaqcData] = useState(null);
  const [resourcesData, setResourcesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (projectId && moduleId) {
      fetchModuleData();
    }
  }, [projectId, moduleId]);

  const fetchModuleData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch project data
      const projectData = await projectService?.getProjectById(projectId);
      setProject(projectData);
      
      // Fetch all dashboard data
      const allData = await projectDashboardService?.getAllDashboardData(projectId);
      
      // Find specific module
      const moduleData = allData?.modules?.find(m => m?.id === moduleId);
      if (!moduleData) {
        throw new Error('Module not found');
      }
      
      setModule(moduleData);
      setProgressData(allData?.progress);
      setQaqcData(allData?.qaqc);
      setResourcesData(allData?.resources || []);
    } catch (err) {
      setError(err?.message || 'Failed to load module data');
      console.error('Error loading module:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToWorkflow = (hasWorkflow) => {
    if (hasWorkflow) {
      // Navigate to view-only page if workflow exists
      navigate(`/project/${projectId}/module/${moduleId}/workflow-view`);
    } else {
      // Navigate to designer if no workflow exists
      navigate(`/project/${projectId}/module/${moduleId}/workflow`);
    }
  };

  const handleBackToDashboard = () => {
    navigate(`/project/${projectId}/dashboard`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'in-progress':
        return 'var(--color-primary)';
      case 'delayed':
        return 'var(--color-destructive)';
      case 'pending':
      default:
        return 'var(--color-warning)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in-progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'delayed':
        return 'Delayed';
      case 'pending':
      default:
        return 'Pending';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[var(--color-background)]">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-60">
          <div className="text-[var(--color-muted-foreground)]">
            <Icon name="loader" className="animate-spin" size={32} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-[var(--color-background)]">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-60">
          <div className="text-center">
            <p className="text-[var(--color-destructive)] mb-4">{error}</p>
            <Button onClick={handleBackToDashboard}>Back to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[var(--color-background)]">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden ml-60">
        <QuickActionsToolbar />
        
        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* Breadcrumb */}
            <div className="mb-4">
              <Breadcrumb
                projectId={projectId}
                projectName={project?.data?.name}
                moduleId={moduleId}
                moduleName={module?.name}
                workflowName={null}
              />
            </div>

            {/* Module Header with Project Logo */}
            <div className="flex items-center gap-4 mb-6">
              {/* Project Logo/Image */}
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-[var(--color-muted)] flex items-center justify-center flex-shrink-0">
                {project?.data?.image_url && !imageError ? (
                  <img
                    src={project?.data?.image_url}
                    alt={project?.data?.name || 'Project'}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <Icon name="folder" size={32} className="text-[var(--color-muted-foreground)]" />
                )}
              </div>

              {/* Module Info */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-[var(--color-foreground)] mb-1">
                  {module?.name || 'Untitled Module'}
                </h1>
              </div>

              {/* Status Badge */}
              <div
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: `${getStatusColor(module?.status)}20`,
                  color: getStatusColor(module?.status)
                }}
              >
                {getStatusLabel(module?.status)}
              </div>

              {/* Action Buttons */}
              <Button
                variant="outline"
                onClick={handleBackToDashboard}
                className="flex items-center gap-2"
              >
                <Icon name="arrow-left" size={16} />
                Back to Dashboard
              </Button>
            </div>

            {/* Dashboard Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-[1400px] mx-auto">
              {/* Workflow Access Card */}
              <div className="lg:col-span-2">
                <WorkflowAccessCard 
                  module={module}
                  onNavigateToWorkflow={handleNavigateToWorkflow}
                />
              </div>

              {/* Progress Summary */}
              <div>
                <ModuleProgressSummary 
                  module={module}
                  progressData={progressData}
                />
              </div>

              {/* QA/QC Tracking */}
              <div>
                <ModuleQAQCTracking 
                  qaqcData={qaqcData}
                />
              </div>

              {/* Resource Management */}
              <div className="lg:col-span-2">
                <ModuleResourceManagement 
                  resources={resourcesData}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleLandingPage;